<template>
  <div class="page-header-container">
    <div class="title-section">
      <div class="title-icon" :style="{ background: iconGradient, boxShadow: iconShadow }">
        <i :class="['bi', icon]"></i>
      </div>
      <div class="title-text">
        <h2 class="page-title" :style="{ color: titleColor }">{{ title }}</h2>
        <p class="page-subtitle">{{ subtitle }}</p>
      </div>
    </div>
    <div class="actions-container">
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    required: true
  },
  color: {
    type: String,
    default: 'blue' // blue, purple, orange, green, teal, pink
  }
});

const colorThemes = {
  blue: {
    gradient: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
    shadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
    title: '#1565C0'
  },
  purple: {
    gradient: 'linear-gradient(135deg, #7B1FA2 0%, #6A1B9A 100%)',
    shadow: '0 4px 12px rgba(123, 31, 162, 0.25)',
    title: '#6A1B9A'
  },
  orange: {
    gradient: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
    shadow: '0 4px 12px rgba(245, 124, 0, 0.25)',
    title: '#E65100'
  },
  green: {
    gradient: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
    shadow: '0 4px 12px rgba(56, 142, 60, 0.25)',
    title: '#2E7D32'
  },
  teal: {
    gradient: 'linear-gradient(135deg, #00897B 0%, #00695C 100%)',
    shadow: '0 4px 12px rgba(0, 137, 123, 0.25)',
    title: '#00695C'
  },
  pink: {
    gradient: 'linear-gradient(135deg, #C2185B 0%, #AD1457 100%)',
    shadow: '0 4px 12px rgba(194, 24, 91, 0.25)',
    title: '#AD1457'
  }
};

const theme = computed(() => colorThemes[props.color] || colorThemes.blue);
const iconGradient = computed(() => theme.value.gradient);
const iconShadow = computed(() => theme.value.shadow);
const titleColor = computed(() => theme.value.title);
</script>

<style scoped>
/* Container principal avec marges standardisées */
.page-header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 1.5rem 0;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #E0E0E0;
}

/* Section titre avec icône */
.title-section {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
}

.title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  flex-shrink: 0;
}

.title-icon i {
  font-size: 1.6rem;
  color: white;
}

.title-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 0.9rem;
  color: #757575;
  margin: 0;
  font-weight: 400;
}

/* Conteneur d'actions */
.actions-container {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
}

/* Responsive design */
@media (max-width: 768px) {
  .page-header-container {
    flex-direction: column;
    align-items: flex-start;
  }

  .actions-container {
    width: 100%;
    justify-content: flex-start;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .title-icon {
    width: 45px;
    height: 45px;
  }

  .title-icon i {
    font-size: 1.4rem;
  }
}

@media (max-width: 576px) {
  .actions-container {
    flex-direction: column;
    width: 100%;
  }
}
</style>
